// Aperçu des factures affichées : totaux, répartition par nature, encaissements.
// Calculé sur la liste filtrée (période, type, statut, recherche).
import { cn } from '../../lib/cn';
import { formatEuros } from '../../services/billing/billingService';
import { LINE_NATURE_LABELS, type LineNature } from '../../lib/billing/lineTotals';
import type { InvoicesSummary } from '../../lib/billing/invoiceList';

export const NATURE_DOT: Record<LineNature, string> = {
  honoraires: 'bg-cime-primary',
  frais: 'bg-cime-gold',
  provision: 'bg-[#2b5aa8]',
};

const COLLECTIONS: { key: keyof InvoicesSummary['collections']; label: string; color: string; hot?: boolean }[] = [
  { key: 'paid', label: 'Réglé', color: 'bg-[#2f7a55]' },
  { key: 'due', label: 'Impayé, non échu', color: 'bg-cime-gold' },
  { key: 'late', label: 'Impayé échu', color: 'bg-cime-urgent', hot: true },
  { key: 'drafts', label: 'Brouillons', color: 'bg-neutral-300' },
];

function PanelCard({ title, aside, children }: { title: string; aside?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-neutral-200 bg-white">
      <header className="flex items-baseline justify-between border-b border-[#e6e2d8] px-4 py-2.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">{title}</h3>
        {aside ? <span className="text-xs text-neutral-400">{aside}</span> : null}
      </header>
      <div className="px-4 py-3.5">{children}</div>
    </section>
  );
}

export function BillingSummaryPanel({ summary }: { summary: InvoicesSummary }) {
  const topNature = Math.max(1, ...summary.byNature.map((n) => n.ht));
  const collectionsTotal = COLLECTIONS.reduce((sum, c) => sum + summary.collections[c.key], 0);

  return (
    <aside aria-label="Aperçu des factures" className="flex flex-col gap-3">
      <PanelCard title="Aperçu" aside={`${summary.count} pièce${summary.count > 1 ? 's' : ''}`}>
        <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 text-[13px]">
          <dt className="text-cime-text-muted">Montant HT</dt>
          <dd className="text-right tabular-nums">{formatEuros(summary.ht)}</dd>
          <dt className="text-cime-text-muted">TVA</dt>
          <dd className="text-right tabular-nums">{formatEuros(summary.vat)}</dd>
          <dt className="border-t border-[#e6e2d8] pt-2 font-semibold text-cime-text-primary">Total TTC</dt>
          <dd className="border-t border-[#e6e2d8] pt-2 text-right font-semibold tabular-nums text-cime-text-primary">
            {formatEuros(summary.ttc)}
          </dd>
        </dl>
      </PanelCard>

      <PanelCard title="Répartition" aside="HT">
        <div className="flex flex-col gap-3">
          {summary.byNature.map((n) => (
            <div key={n.nature}>
              <div className="flex items-baseline justify-between gap-2 text-[13px]">
                <span className="inline-flex items-center gap-[7px] text-cime-text-primary">
                  <i aria-hidden className={cn('h-[7px] w-[7px] rounded-[2px]', NATURE_DOT[n.nature])} />
                  {LINE_NATURE_LABELS[n.nature]}
                </span>
                <b className="font-semibold tabular-nums">
                  {formatEuros(n.ht)}
                  <small className="ml-1.5 font-normal text-neutral-400">{n.share} %</small>
                </b>
              </div>
              <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-neutral-100">
                <i className={cn('block h-full rounded-full', NATURE_DOT[n.nature])} style={{ width: `${(n.ht / topNature) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </PanelCard>

      <PanelCard title="Encaissements" aside="TTC">
        <div className="mb-3 flex h-2 overflow-hidden rounded-full bg-neutral-100" aria-hidden>
          {collectionsTotal > 0
            ? COLLECTIONS.map((c) => (
                <i
                  key={c.key}
                  className={cn('block h-full', c.color)}
                  style={{ width: `${(summary.collections[c.key] / collectionsTotal) * 100}%` }}
                />
              ))
            : null}
        </div>
        <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 text-[13px]">
          {COLLECTIONS.map((c) => {
            const value = summary.collections[c.key];
            return (
              <div key={c.key} className="contents">
                <dt className="inline-flex items-center gap-[7px] text-cime-text-muted">
                  <i aria-hidden className={cn('h-[7px] w-[7px] rounded-[2px]', c.color)} />
                  {c.label}
                </dt>
                <dd className={cn('text-right tabular-nums', c.hot && value > 0 ? 'font-semibold text-cime-urgent' : 'text-cime-text-primary')}>
                  {formatEuros(value)}
                </dd>
              </div>
            );
          })}
        </dl>
      </PanelCard>
    </aside>
  );
}
